<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
  } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { currencySymbol, parseMoneyInput } from '@/domain/store-setup/money';
  import { ICategory } from '@/interfaces/ICategory';

  /**
   * «+ Agregar producto a {categoría}» (plan §5.9, desktop and tablet): it
   * expands inline into «Nombre» + «Precio» + «Agregar». Enter adds and the
   * fields stay focused for the next one; an empty price creates the
   * product hidden, an explicit 0 keeps it visible.
   */
  const props = withDefaults(
    defineProps<{
      category: ICategory;
      /** Starts expanded (an empty category). */
      startOpen?: boolean;
      /** Focuses the name once mounted (a category just created). */
      autofocus?: boolean;
    }>(),
    { startOpen: false, autofocus: false }
  );

  const { state, data, access, online, actions } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const root = ref<HTMLElement | null>(null);
  const open = ref(props.startOpen);
  const name = ref('');
  const priceText = ref('');
  const saving = ref(false);
  const triedPrice = ref(false);
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  const symbol = computed(() => currencySymbol(data.rowContext.value.currency));
  const price = computed(() =>
    priceText.value.trim()
      ? parseMoneyInput(priceText.value, { allowZero: true })
      : null
  );
  const priceInvalid = computed(
    () => !!priceText.value.trim() && price.value === null
  );
  /**
   * Without a price the product starts hidden everywhere, which only roles
   * that manage every branch can undo: the others must write one.
   */
  const priceRequired = computed(() => !access.value.canHideEverywhere);
  const priceMissing = computed(
    () => priceRequired.value && !priceText.value.trim()
  );
  const hint = computed(() => {
    if (triedPrice.value && priceInvalid.value)
      return { text: t('menuPage.price.invalid'), error: true };
    if (priceRequired.value)
      return {
        text: t('menuPage.quickAdd.priceRequired'),
        error: triedPrice.value && priceMissing.value,
      };
    return { text: t('menuPage.quickAdd.hint'), error: false };
  });
  const canAdd = computed(
    () =>
      !!name.value.trim() &&
      !priceInvalid.value &&
      !priceMissing.value &&
      !saving.value &&
      online.value
  );

  function focusName() {
    root.value
      ?.querySelector<HTMLInputElement>('.quick-name input')
      ?.focus({ preventScroll: true });
  }

  async function expand() {
    open.value = true;
    await nextTick();
    focusName();
  }

  /** Closes the form; the focus goes back to «+ Agregar producto…». */
  async function collapse() {
    open.value = false;
    name.value = '';
    priceText.value = '';
    triedPrice.value = false;
    await nextTick();
    root.value
      ?.querySelector<HTMLButtonElement>('.quick-open')
      ?.focus({ preventScroll: true });
  }

  async function add() {
    triedPrice.value = true;
    if (!canAdd.value) return;
    saving.value = true;
    try {
      const created = await actions.quickAdd(
        props.category,
        name.value,
        price.value
      );
      // A filter or a store switch may have unmounted the card meanwhile.
      if (unmounted || !created) return;
      name.value = '';
      priceText.value = '';
      triedPrice.value = false;
      await nextTick();
      if (!unmounted) focusName();
    } finally {
      if (!unmounted) saving.value = false;
    }
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && !props.startOpen) {
      event.stopPropagation();
      void collapse();
    }
  }

  onMounted(() => {
    if (!props.autofocus) return;
    focusName();
    state.focusQuickAddCategoryId = null;
  });
</script>

<template>
  <div ref="root" class="v2-catalog-quick-add">
    <button
      v-if="!open"
      type="button"
      class="quick-open"
      :disabled="!online"
      :title="online ? undefined : t('menuPage.errors.offline')"
      @click="expand"
    >
      <i class="mdi mdi-plus open-icon" />
      <span class="open-label">{{
        t('menuPage.quickAdd.open', { category: category.name })
      }}</span>
    </button>

    <form
      v-else
      class="quick-form"
      :class="{ 'has-close': !startOpen }"
      novalidate
      @submit.prevent="add"
      @keydown="onKeydown"
    >
      <PosUiInput
        v-model="name"
        class="quick-name"
        :maxlength="128"
        :placeholder="t('menuPage.quickAdd.name')"
        :aria-label="t('menuPage.quickAdd.name')"
        autocomplete="off"
      />
      <div class="quick-price">
        <PosUiInput
          v-model="priceText"
          :prefix="symbol"
          inputmode="decimal"
          :placeholder="t('menuPage.quickAdd.price')"
          :aria-label="t('menuPage.quickAdd.price')"
          :aria-describedby="`quick-add-hint-${category.id}`"
          autocomplete="off"
        />
      </div>
      <PosUiButton
        class="quick-submit"
        type="submit"
        color="neutral"
        :disabled="!canAdd"
        :title="online ? undefined : t('menuPage.errors.offline')"
        >{{ t('menuPage.quickAdd.add') }}</PosUiButton
      >
      <button
        v-if="!startOpen"
        type="button"
        class="quick-close"
        :aria-label="t('close')"
        @click="collapse()"
      >
        <i class="mdi mdi-close" />
      </button>
      <p
        :id="`quick-add-hint-${category.id}`"
        class="quick-hint"
        :class="{ 'is-error': hint.error }"
      >
        {{ hint.text }}
      </p>
    </form>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-quick-add {
    @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100;

    .quick-open {
      @apply tw-flex tw-min-h-[48px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-b-xl tw-border-0 tw-bg-transparent tw-px-4 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors tw-duration-150;

      &:hover:not(:disabled) {
        @apply tw-bg-neutral-50 tw-text-neutral-900;
      }

      &:disabled {
        @apply tw-cursor-default tw-text-neutral-400;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      .open-icon {
        @apply tw-mr-2 tw-text-lg tw-leading-none;
      }

      .open-label {
        @apply tw-min-w-0 tw-truncate;
      }
    }

    // Margins, not flex gap (Chrome < 84). The ✕ sits at the top right,
    // outside the wrapping row: beside the name when the row wraps.
    .quick-form {
      @apply tw-relative tw-flex tw-flex-wrap tw-items-center tw-px-3 tw-pb-2 tw-pt-3;

      &.has-close {
        @apply tw-pr-14;
      }

      .quick-name {
        @apply tw-mb-1 tw-min-w-0 tw-flex-1;
        flex-basis: 200px;
      }

      .quick-price {
        @apply tw-mb-1 tw-ml-2 tw-w-36 tw-flex-none;
      }

      .quick-submit {
        @apply tw-mb-1 tw-ml-2;
      }

      .quick-close {
        @apply tw-absolute tw-right-2 tw-top-3 tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

        &:hover {
          @apply tw-bg-neutral-100 tw-text-neutral-800;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }

      .quick-hint {
        @apply tw-m-0 tw-w-full tw-text-xs tw-text-neutral-500;

        &.is-error {
          @apply tw-text-red-600;
        }
      }
    }
  }
</style>
